// src/App.tsx
import "../Report.css";

import EmissionsBreakdownChart from "@/pages/communication/common/EmissionsBreakdownChart";

import ReportHeader from "@/components/ReportHeader";
import ReportFooter from "@/components/ReportFooter";
import { Cloud, Gauge, Flame, Car, FireExtinguisher, Zap } from "lucide-react";
import { DataPill } from "@/pages/communication/common/DataPill";
import { companyAtom } from "@/store/company";
import { datesAtom } from "@/store/dates";
import { useAtom } from "jotai";
import { Category, CompanySummary } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { getApiUrl } from "@/utils/api";
import { useAuth } from "@/hooks/useAuth";
import { categoriesAtom } from "@/store/categories";
import { levelsAtom } from "@/store/levels";
import { intensityAtom } from "@/store/intensity";

interface IntroPageProps {
  title: string;
  page?: number;
}

export const ResumenEjectutivoPage = ({ title, page }: IntroPageProps) => {
  const { token } = useAuth();

  const [company] = useAtom(companyAtom);
  const [dates] = useAtom(datesAtom);
  const [categories] = useAtom(categoriesAtom);
  const [levels] = useAtom(levelsAtom);
  const [intensity] = useAtom(intensityAtom);

  const { data: categoriesData } = useQuery<any>({
    queryKey: ["categories", categories?.join(",")],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl(`categories/?ids=${categories?.join(",")}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch data types");
      }
      return response.json();
    },
    enabled: !!token && !!categories,
  });

  const { data } = useQuery<CompanySummary>({
    queryKey: [
      "emissions-summary",
      company?.id,
      dates?.fromDate,
      dates?.toDate,
    ],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl(
          `companies/${company?.id}/emissions-summary/?start_date=${dates?.fromDate}&end_date=${dates?.toDate}&categories=${categories?.join(",")}`,
        ),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch data types");
      }
      return response.json();
    },
    enabled: !!token && !!company && !!dates,
  });

  return (
    <section className="pdf-page">
      <ReportHeader title={title} />
      <div className="pdf-content text-xl">
        <div className="px-2 grid grid-cols-2 gap-4 w-[680px]">
          <DataPill
            title="Emisiones totales"
            value={String(data?.emissions.toFixed(2) || "-")}
            unit="ton CO2eq"
            icon={<Cloud className="h-8 w-8 text-primary" />}
          />
          {intensity?.value ? (
            <DataPill
              title="Intensidad"
              value={String(
                ((data?.emissions || 0) / (intensity?.value || 1)).toFixed(2) ||
                  "-",
              )}
              unit={`ton CO2eq/${intensity?.unit?.slice(0, 2)}`}
              icon={<Gauge className="h-8 w-8 text-primary" />}
            />
          ) : (
            <div />
          )}
          {levels?.includes(1) && (
            <DataPill
              title="Alcance 1"
              value={String(data?.alcance_1.toFixed(2) || "")}
              unit="ton CO2e"
              icon={<Cloud className="h-8 w-8 text-primary" />}
            />
          )}
          {levels?.includes(2) && (
            <DataPill
              title="Alcance 2"
              value={String(data?.alcance_2.toFixed(2) || "")}
              unit="ton CO2e"
              icon={<Cloud className="h-8 w-8 text-primary" />}
            />
          )}
        </div>
        <div className="mt-16 w-[800px]">
          <EmissionsBreakdownChart
            data={[
              {
                icon: <Flame className="h-6 w-6" />,
                label: "Combustión",
                value:
                  Number(data?.categories?.combustion.toFixed(2)) / 1000 || 0,
                type: "combustion",
              },
              {
                icon: <Car className="h-6 w-6" />,
                label: "Vehículos de empresa",
                value: Number(data?.categories?.consumption.toFixed(2)) || 0,
                type: "vehicles",
              },
              {
                icon: <FireExtinguisher className="h-6 w-6" />,
                label: "Recargas",
                value: Number(data?.categories?.rechages.toFixed(2)) || 0,
                type: "recharges",
              },
              {
                icon: <Zap className="h-6 w-6" />,
                label: "Electricidad",
                value: Number(data?.categories?.electricity.toFixed(2)) || 0,
                type: "electricity",
              },
            ].filter(
              (item) =>
                categoriesData?.results?.find(
                  (category: Category) =>
                    category.name.toLowerCase() === item.type.toLowerCase(),
                )?.id > 0,
            )}
          />
        </div>

        {/* <div className="mt-12">
          <EmissionsBreakdownChart
            title="Sede 2"
            titleIconName="Building"
            titleDetails={
              <>
                <span className="ml-2 text-red-600">[nombre de la sede]</span>
                <span className="ml-auto">240 ton CO2e</span>
              </>
            }
            data={[
              { iconName: "Flame", label: "Combustión", value: 6 },
              { iconName: "Car", label: "Vehículos de empresa", value: 14 },
              { iconName: "FireExtinguisher", label: "Recargas", value: 18 },
              { iconName: "Zap", label: "Electricidad", value: 16 },
            ]}
          />
        </div> */}
      </div>
      <ReportFooter page={page} />
    </section>
  );
};
